/**
 * useAdminInbox — 내 업무 홈 "관리" 블록의 처리 대기 숫자 (B′ 미리보기, 2026-10-19).
 *
 * 상단바 알림 벨(hooks/useNotifications.ts)과 **같은 소스**를 읽는다(새 컬렉션 없음):
 *   - 가입 승인 대기 : users 구독 → 그룹 미지정 + 비활성 (UsersSection.isPending 과 같은 판정). 관리자만.
 *   - 영업 동기화 실패 : sales_sync_status/latest 의 ok === false
 *   - 출하량 불일치    : 최신 rqc_drift_log 의 drift_count
 * 항목마다 켜는 조건을 따로 받는다 — rules 가 읽기를 막는 항목을 구독하면 permission-denied + Firestore ca9 오류가 난다
 * (10-26: 생산 부서 중앙 법인 관리자가 sales_sync_status·users 를 구독하던 문제). 꺼진 항목은 구독하지 않고 null.
 * 알림 벨은 표시 설정(on/off)을 따르지만, 여기는 관리자가 직접 보는 요약이라 설정과 무관하게 센다.
 */
import { useEffect, useState } from "react";
import { usersRepo } from "@/services/users.repository";
import { useSalesSyncStatus } from "@/hooks/useSalesInvoices";
import { useRqcDriftStatus } from "@/hooks/useRqcDriftStatus";

const isPending = (r: { role?: string | null; active?: boolean; deleted?: boolean }) => !r.deleted && !r.role && !r.active;

export interface AdminInbox {
  /** 가입 승인 대기 수. null = 불러오는 중 */
  pendingSignups: number | null;
  /** 영업 동기화 마지막 실행이 실패했나. null = 상태 문서 없음/불러오는 중 */
  salesSyncFailed: boolean | null;
  salesSyncError: string | null;
  /** 최신 드리프트 검사에서 불일치한 개월 수. null = 기록 없음/불러오는 중 */
  driftMonths: number | null;
}

export interface AdminInboxScope {
  /** 가입 승인 대기 — users 전체 읽기(rules: 전체 admin 만) */
  signups: boolean;
  /** 영업 동기화 상태 — rules: sales.view */
  salesSync: boolean;
  /** 출하량 불일치 — rules: shipment.view 또는 dispatch.view */
  drift: boolean;
}

export function useAdminInbox({ signups, salesSync, drift: driftOn }: AdminInboxScope): AdminInbox {
  const [pending, setPending] = useState<{ n: number } | null>(null);
  useEffect(() => {
    if (!signups) return;
    return usersRepo.watch(
      (rows) => setPending({ n: rows.filter(isPending).length }),
      () => setPending(null),
    );
  }, [signups]);

  const sync = useSalesSyncStatus(salesSync);
  const drift = useRqcDriftStatus(driftOn);

  return {
    pendingSignups: signups && pending ? pending.n : null,
    salesSyncFailed: sync ? sync.ok === false : null,
    salesSyncError: sync && sync.ok === false ? (sync.error || null) : null,
    driftMonths: drift ? drift.driftCount : null,
  };
}
